import { XRPC, simpleFetchHandler, XRPCError } from '@atcute/client';
import { segmentize } from '@atcute/bluesky-richtext-segmenter';

const ATTR_REGEX = /[&"<]/g;
const CONTENT_REGEX = /[&<]/g;

/**
 * @template V
 * @param {V} value
 * @param {boolean} [is_attr]
 */
function escape_html(value, is_attr) {
	const str = String(value ?? '');

	const pattern = is_attr ? ATTR_REGEX : CONTENT_REGEX;
	pattern.lastIndex = 0;

	let escaped = '';
	let last = 0;

	while (pattern.test(str)) {
		const i = pattern.lastIndex - 1;
		const ch = str[i];
		escaped += str.substring(last, i) + (ch === '&' ? '&amp;' : ch === '"' ? '&quot;' : '&lt;');
		last = i + 1;
	}

	return escaped + str.substring(last);
}

/**
 * @template V
 * @param {string} name
 * @param {V} value
 * @param {boolean} [is_boolean]
 * @returns {string}
 */
function attr(name, value, is_boolean = false) {
	if (value == null || (!value && is_boolean) || (value === '' && name === 'class')) return '';
	const normalized = value;
	const assignment = is_boolean ? '' : `="${escape_html(normalized, true)}"`;
	return ` ${name}${assignment}`;
}

/** @import { ComponentType, SvelteComponent } from 'svelte' */
/** @import { Component, Payload, RenderOutput } from '#server' */
/** @import { Store } from '#shared' */

/**
 * Only available on the server and when compiling with the `server` option.
 * Takes a component and returns an object with `body` and `head` properties on it, which you can use to populate the HTML when server-rendering your app.
 * @template {Record<string, any>} Props
 * @param {import('svelte').Component<Props> | ComponentType<SvelteComponent<Props>>} component
 * @param {{ props?: Omit<Props, '$$slots' | '$$events'>; context?: Map<any, any> }} [options]
 * @returns {RenderOutput}
 */
function render(component, options) {
	/** @type {Payload} */
	const payload = { out: '' };
	component(payload, options?.props ?? {});

	return {
		head: '',
		body: payload.out
	};
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function stringify(value) {
	return typeof value === 'string' ? value : value == null ? '' : value + '';
}

/** @param {any} array_like_or_iterator */
function ensure_array_like(array_like_or_iterator) {
	return array_like_or_iterator;
}

const DEFAULT_APPVIEW_URL = "https://public.api.bsky.app";
const NO_UNAUTHENTICATED_LABEL = "!no-unauthenticated";

function Embed_frame($$payload, $$props) {
	

	const { children } = $$props;

	$$payload.out += `<div class="bluesky-embed s-3olstj">`;
	children($$payload);
	$$payload.out += `</div>`;
	
}

const getProfileUrl = (author) => {
  return `https://bsky.app/profile/${author}`;
};
const getPostUrl = (author, rkey) => {
  return `https://bsky.app/profile/${author}/post/${rkey}`;
};
const getHashtagUrl = (tag) => {
  return `https://bsky.app/hashtag/${tag}`;
};
const getFeedUrl = (author, rkey) => {
  return `https://bsky.app/profile/${author}/feed/${rkey}`;
};
const getStarterpackUrl = (author, rkey) => {
  return `https://bsky.app/starter-pack/${author}/${rkey}`;
};
const getStarterpackImgUrl = (author, rkey) => {
  return `https://ogcard.cdn.bsky.app/start/${author}/${rkey}`;
};

let startOfYear = 0;
let endOfYear = 0;
const fmtAbsoluteLong = new Intl.DateTimeFormat("en-US", { dateStyle: "long", timeStyle: "short" });
const fmtAbsShortWithYear = new Intl.DateTimeFormat("en-US", { dateStyle: "medium" });
const fmtAbsShort = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" });
const formatShortDate = (date) => {
  const inst = new Date(date);
  const time = inst.getTime();
  if (isNaN(time)) {
    return "N/A";
  }
  const now = Date.now();
  if (now > endOfYear) {
    const date2 = new Date(now);
    date2.setMonth(0, 1);
    date2.setHours(0, 0, 0);
    startOfYear = date2.getTime();
    date2.setFullYear(date2.getFullYear() + 1, 0, 0);
    date2.setHours(23, 59, 59, 999);
    endOfYear = date2.getTime();
  }
  if (time >= startOfYear && time <= endOfYear) {
    return fmtAbsShort.format(inst);
  }
  return fmtAbsShortWithYear.format(inst);
};
const formatLongDate = (date) => {
  const inst = new Date(date);
  if (isNaN(inst.getTime())) {
    return "N/A";
  }
  return fmtAbsoluteLong.format(inst);
};

const FlagsNone = 0;
const FlagsNoSelf = 1 << 0;
const LABEL_MAPPING = {
  "!hide": {
    name: `Hidden by moderators`,
    flags: FlagsNoSelf
  },
  "!warn": {
    name: `Content warning`,
    flags: FlagsNoSelf
  },
  porn: {
    name: `Adult content`,
    flags: FlagsNone
  },
  sexual: {
    name: `Sexually suggestive`,
    flags: FlagsNone
  },
  "graphic-media": {
    name: `Graphic media`,
    flags: FlagsNone
  },
  nudity: {
    name: `Nudity`,
    flags: FlagsNone
  }
};
const findLabel = (labels, authorDid) => {
  if (labels?.length) {
    for (let idx = 0, len = labels.length; idx < len; idx++) {
      const label = labels[idx];
      const val = label.val;
      if (!(val in LABEL_MAPPING)) {
        continue;
      }
      const def = LABEL_MAPPING[val];
      if (def.flags & FlagsNoSelf && label.src === authorDid) {
        continue;
      }
      return def;
    }
  }
};

const long = new Intl.NumberFormat("en-US");
const compact = new Intl.NumberFormat("en-US", { notation: "compact" });
const formatCompactNumber = (value) => {
  if (value < 1e3) {
    return "" + value;
  }
  if (value < 1e5) {
    return long.format(value);
  }
  return compact.format(value);
};
const formatLongNumber = (value) => {
  return long.format(value);
};

const AT_URI_RE = /^at:\/\/((?:did:[a-zA-Z0-9._:%-]+)|(?:[a-zA-Z0-9][a-zA-Z0-9-.]*))(?:\/([a-zA-Z0-9.-]+)(?:\/([a-zA-Z0-9_~.:-]{1,512}))?)?\/?(?:\?([^#\s]*))?(?:#([^\s]*))?$/;
const parseAtUri = (str) => {
  const match = AT_URI_RE.exec(str);
  if (!match) {
    throw new InvalidAtUriError(`invalid at-uri: ${str}`);
  }
  return {
    repo: match[1],
    collection: match[2] ?? "",
    rkey: match[3] ?? "",
    query: match[4] ?? "",
    fragment: match[5] ?? ""
  };
};
class InvalidAtUriError extends Error {
}

function Content_hider($$payload, $$props) {
	

	const { warning, children } = $$props;

	if (!warning) {
		
		children($$payload);
		
	} else {
		
		$$payload.out += `<details class="content-hider s-q6x07m"><summary class="gate s-q6x07m"><svg class="icon s-q6x07m" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" stroke-linecap="square" stroke-width="2" d="M11 11h1v5m9-4a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"></path><path fill="currentColor" stroke="currentColor" stroke-width=".5" d="M11.5 7.25h-.25v1.5h1.5v-1.5H11.5Z"></path></svg> <span class="label s-q6x07m">${escape_html(warning.name)}</span> <span class="action s-q6x07m"></span></summary> `;
		children($$payload);
		$$payload.out += `</details>`;
	}

	
	
}

const safeParseUrl = (str) => {
	let url;

	if ('parse' in URL) {
		url = URL.parse(str);
	} else {
		try {
			// @ts-expect-error: `'parse' in URL` is giving truthy
			url = new URL(str);
		} catch {}
	}

	if (url && (url.protocol === 'https:' || url.protocol === 'http:')) {
		return url;
	}

	return null;
};

function External_embed($$payload, $$props) {
	

	const { embed } = $$props;
	const external = embed.external;
	const domain = safeParseUrl(external.uri)?.host;

	$$payload.out += `<a target="_blank"${attr("href", domain && external.uri)} rel="noopener noreferrer nofollow" class="external-embed s-rtbqd8">`;

	if (external.thumb) {
		
		$$payload.out += `<img loading="lazy"${attr("src", external.thumb)} alt="" class="thumbnail s-rtbqd8">`;
	}

	$$payload.out += ` <div class="meta s-rtbqd8"><p class="title s-rtbqd8">${escape_html(external.title)}</p> <p class="description s-rtbqd8">${escape_html(external.description)}</p> `;

	if (domain) {
		
		$$payload.out += `<div class="domain s-rtbqd8"><svg class="icon" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" stroke-linecap="round" stroke-width="2" d="m4.172 8.07 3.94 2.957.977-1.941 3.887-.978 1.15-4.6M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-6.078 4.865.973-1.946-2.869-1.928-1.89-.12-1.08 1.075 1.947 2.919h2.919Z"></path></svg> <span class="domain-name">${escape_html(domain)}</span></div>`;
	}

	$$payload.out += `</div></a>`;
	
}

function Feed_embed($$payload, $$props) {
	

	const { embed: feed } = $$props;
	const creator = feed.creator;
	const feedUrl = getFeedUrl(creator.did, parseAtUri(feed.uri).rkey);

	$$payload.out += `<a target="_blank"${attr("href", feedUrl)} class="feed-embed s-156tlwp"><div class="main s-156tlwp"><div class="avatar-wrapper s-156tlwp">`;

	if (feed.avatar) {
		
		$$payload.out += `<img loading="lazy"${attr("src", feed.avatar)} alt="" class="avatar s-156tlwp">`;
	} else {
		
		$$payload.out += `<svg viewBox="0 0 32 32" class="avatar s-156tlwp"><path fill="#0070FF" d="M0 0h32v32H0z"></path><path fill="#fff" d="M22.153 22.354a9.328 9.328 0 0 0 3.837-.491 3.076 3.076 0 0 0-4.802-2.79m.965 3.281a6.128 6.128 0 0 0-.965-3.28Zm-11.342-3.28a3.077 3.077 0 0 0-4.801 2.79 9.21 9.21 0 0 0 3.835.49m.966-3.28a6.127 6.127 0 0 0-.966 3.28Zm8.265-8.997a3.076 3.076 0 1 1-6.153 0 3.076 3.076 0 0 1 6.153 0Zm6.154 3.077a2.307 2.307 0 1 1-4.615 0 2.307 2.307 0 0 1 4.615 0Zm-13.847 0a2.307 2.307 0 1 1-4.614 0 2.307 2.307 0 0 1 4.614 0Z"></path><path fill="#fff" d="M22 22c0 3.314-2.686 3.5-6 3.5s-6-.186-6-3.5a6 6 0 0 1 12 0Z"></path></svg>`;
	}

	$$payload.out += `</div> <div class="info"><p class="name s-156tlwp">${escape_html(feed.displayName)}</p> <p class="creator s-156tlwp">Feed by @${escape_html(creator.handle)}</p></div></div> <p class="description s-156tlwp">${escape_html(feed.description)}</p></a>`;
	
}

const DEFAULT_RATIO = { width: 16, height: 9 };

function Image_embed($$payload, $$props) {
	

	const { embed, borderless, standalone, blur } = $$props;
	const images = embed.images;
	const length = images.length;

	function Image($$payload, index) {
		const image = images[index];

		$$payload.out += `<img loading="lazy"${attr("src", image.thumb)}${attr("alt", image.alt)}${attr("class", `${stringify(`image` + (blur ? ` is-blurred` : ``))} s-t3k6fc`)}>`;
	}

	$$payload.out += `<div${attr("class", `${stringify('image-embed' + (!borderless ? ` is-bordered` : ``) + (standalone && length === 1 ? ` is-aligned` : ``))} s-t3k6fc`)}>`;

	if (length === 4) {
		
		$$payload.out += `<div class="grid s-t3k6fc"><div class="col s-t3k6fc"><div class="item wide tl s-t3k6fc">`;
		Image($$payload, 0);
		$$payload.out += `</div> <div class="item wide bl s-t3k6fc">`;
		Image($$payload, 2);
		$$payload.out += `</div></div> <div class="col s-t3k6fc"><div class="item wide tr s-t3k6fc">`;
		Image($$payload, 1);
		$$payload.out += `</div> <div class="item wide br s-t3k6fc">`;
		Image($$payload, 3);
		$$payload.out += `</div></div></div>`;
	} else {
		

		if (length === 3) {
			
			$$payload.out += `<div class="grid s-t3k6fc"><div class="col square s-t3k6fc"><div class="item tl bl s-t3k6fc">`;
			Image($$payload, 0);
			$$payload.out += `</div></div> <div class="col square s-t3k6fc"><div class="item tr s-t3k6fc">`;
			Image($$payload, 1);
			$$payload.out += `</div> <div class="item br s-t3k6fc">`;
			Image($$payload, 2);
			$$payload.out += `</div></div></div>`;
		} else {
			

			if (length === 2) {
				
				$$payload.out += `<div class="grid s-t3k6fc"><div class="col s-t3k6fc"><div class="item square tl bl s-t3k6fc">`;
				Image($$payload, 0);
				$$payload.out += `</div></div> <div class="col s-t3k6fc"><div class="item square tr br s-t3k6fc">`;
				Image($$payload, 1);
				$$payload.out += `</div></div></div>`;
			} else {
				

				if (length === 1) {
					

					const ratio = standalone && (images[0].aspectRatio || DEFAULT_RATIO);

					$$payload.out += `<div${attr("class", `${stringify(`single-item tl tr bl br` + (ratio ? ` is-standalone` : ``))} s-t3k6fc`)}${attr("style", ratio ? `aspect-ratio: ${ratio.width}/${ratio.height}` : ``)}>`;
					Image($$payload, 0);
					$$payload.out += ` `;

					if (ratio) {
						
						$$payload.out += `<div class="placeholder s-t3k6fc"></div>`;
					}

					$$payload.out += `</div>`;
				}

				
			}

			
		}

		
	}

	$$payload.out += `</div>`;
	
}

const getPurpose = (purpose) => {
	switch (purpose) {
		case 'app.bsky.graph.defs#curatelist':
			return `User list`;

		case 'app.bsky.graph.defs#modlist':
			return `Moderation list`;
	}
	return `Unknown list`;
};

function List_embed($$payload, $$props) {
	

	const { embed: list } = $$props;
	const creator = list.creator;
	const listUrl = getFeedUrl(creator.did, parseAtUri(list.uri).rkey);

	$$payload.out += `<a target="_blank"${attr("href", listUrl)} class="list-embed s-6uize5"><div class="main s-6uize5"><div class="avatar-wrapper s-6uize5">`;

	if (list.avatar) {
		
		$$payload.out += `<img loading="lazy"${attr("src", list.avatar)} alt="" class="avatar s-6uize5">`;
	} else {
		
		$$payload.out += `<svg viewBox="0 0 32 32" class="avatar s-6uize5"><path fill="#0070FF" d="M0 0h32v32H0z"></path><path fill="#fff" d="M22.153 22.354a9.328 9.328 0 0 0 3.837-.491 3.076 3.076 0 0 0-4.802-2.79m.965 3.281a6.128 6.128 0 0 0-.965-3.28Zm-11.342-3.28a3.077 3.077 0 0 0-4.801 2.79 9.21 9.21 0 0 0 3.835.49m.966-3.28a6.127 6.127 0 0 0-.966 3.28Zm8.265-8.997a3.076 3.076 0 1 1-6.153 0 3.076 3.076 0 0 1 6.153 0Zm6.154 3.077a2.307 2.307 0 1 1-4.615 0 2.307 2.307 0 0 1 4.615 0Zm-13.847 0a2.307 2.307 0 1 1-4.614 0 2.307 2.307 0 0 1 4.614 0Z"></path><path fill="#fff" d="M22 22c0 3.314-2.686 3.5-6 3.5s-6-.186-6-3.5a6 6 0 0 1 12 0Z"></path></svg>`;
	}

	$$payload.out += `</div> <div class="info"><p class="name s-6uize5">${escape_html(list.name)}</p> <p class="creator s-6uize5">${escape_html(getPurpose(list.purpose))} by @${escape_html(creator.handle)}</p></div></div> <p class="description s-6uize5">${escape_html(list.description)}</p></a>`;
	
}

function Video_embed($$payload, $$props) {
	

	const {
		post,
		embed: video,
		borderless,
		standalone,
		blur
	} = $$props;

	const ratio = standalone && video.aspectRatio;
	const postUrl = post && getPostUrl(post.author.did, parseAtUri(post.uri).rkey);

	function Content($$payload) {
		$$payload.out += `<img loading="lazy"${attr("src", video.thumbnail)} alt=""${attr("class", `${stringify(`thumbnail` + (blur ? ` is-blurred` : ``))} s-1azk58e`)}> `;

		if (ratio) {
			
			$$payload.out += `<div class="placeholder s-1azk58e"></div>`;
		}

		$$payload.out += ` <div class="play s-1azk58e"><svg class="icon s-1azk58e" fill="none" viewBox="0 0 24 24"><path fill="currentColor" d="M22 12 5 2v20l17-10Z"></path></svg></div>`;
	}

	if (standalone) {
		
		$$payload.out += `<a target="_blank"${attr("href", postUrl)}${attr("class", `${stringify(`video-embed` + (!borderless ? ` is-bordered` : ``) + (standalone ? ` is-standalone` : ``))} s-1azk58e`)}><div class="constrainer s-1azk58e"${attr("style", ratio ? `aspect-ratio: ${ratio.width}/${ratio.height}` : ``)}>`;
		Content($$payload);
		$$payload.out += `</div></a>`;
	} else {
		
		$$payload.out += `<div${attr("class", `${stringify(`video-embed` + (!borderless ? ` is-bordered` : ``))} s-1azk58e`)}${attr("style", ratio ? `aspect-ratio: ${ratio.width}/${ratio.height}` : ``)}>`;
		Content($$payload);
		$$payload.out += `</div>`;
	}

	
	
}

const getPostImage = (embed) => {
	if (embed) {
		if (embed.$type === 'app.bsky.embed.images#view') {
			return embed;
		}

		if (embed.$type === 'app.bsky.embed.recordWithMedia#view') {
			return getPostImage(embed.media);
		}
	}
};

const getPostVideo = (embed) => {
	if (embed) {
		if (embed.$type === 'app.bsky.embed.video#view') {
			return embed;
		}

		if (embed.$type === 'app.bsky.embed.recordWithMedia#view') {
			return getPostVideo(embed.media);
		}
	}
};

function Quote_embed($$payload, $$props) {
	

	const { embed: quote, large = false } = $$props;
	const record = quote.value;
	const text = record.text.trim();
	const author = quote.author;
	const authorName = author.displayName?.trim();
	const embed = quote.embeds?.[0];
	const image = getPostImage(embed);
	const video = getPostVideo(embed);
	const postUrl = getPostUrl(author.did, parseAtUri(quote.uri).rkey);
	const isMediaBlurred = !!findLabel(quote.labels, author.did);

	$$payload.out += `<a target="_blank"${attr("href", postUrl)} class="quote-embed s-za6fip"><div class="meta s-za6fip"><div class="avatar-wrapper s-za6fip">`;

	if (author.avatar) {
		
		$$payload.out += `<img loading="lazy"${attr("src", author.avatar)} alt="" class="avatar s-za6fip">`;
	}

	$$payload.out += `</div> <span class="name-wrapper s-za6fip">`;

	if (authorName) {
		
		$$payload.out += `<bdi class="display-name-wrapper s-za6fip"><span class="display-name s-za6fip">${escape_html(authorName)}</span></bdi>`;
	}

	$$payload.out += ` <span class="handle s-za6fip">@${escape_html(author.handle)}</span></span> <span aria-hidden="true" class="dot s-za6fip">·</span> <time${attr("datetime", record.createdAt)} class="date s-za6fip">${escape_html(formatShortDate(record.createdAt))}</time></div> `;

	if (text) {
		
		$$payload.out += `<div class="body s-za6fip">`;

		if (!large) {
			

			if (image) {
				
				$$payload.out += `<div class="aside s-za6fip">`;
				Image_embed($$payload, { embed: image, blur: isMediaBlurred });
				$$payload.out += `</div>`;
			} else {
				

				if (video) {
					
					$$payload.out += `<div class="aside s-za6fip">`;
					Video_embed($$payload, { embed: video, blur: isMediaBlurred });
					$$payload.out += `</div>`;
				}

				
			}

			
		}

		$$payload.out += ` <p class="text s-za6fip">${escape_html(text)}</p></div>`;
	} else {
		
		$$payload.out += `<div class="divide s-za6fip"></div>`;
	}

	$$payload.out += ` `;

	if (large || !text) {
		

		if (image) {
			

			Image_embed($$payload, {
				embed: image,
				borderless: true,
				blur: isMediaBlurred
			});
		} else {
			

			if (video) {
				

				Video_embed($$payload, {
					embed: video,
					borderless: true,
					blur: isMediaBlurred
				});
			}

			
		}

		
	}

	$$payload.out += `</a>`;
	
}

function Starterpack_embed($$payload, $$props) {
	

	const { embed: pack, large = false } = $$props;
	const record = pack.record;
	const creator = pack.creator;
	const creatorDid = creator.did;
	const rkey = parseAtUri(pack.uri).rkey;
	const packUrl = getStarterpackUrl(creatorDid, rkey);

	$$payload.out += `<a target="_blank"${attr("href", packUrl)} class="starterpack-embed s-15v965v">`;

	if (large) {
		

		const imageUrl = getStarterpackImgUrl(creatorDid, rkey);

		$$payload.out += `<img loading="lazy"${attr("src", imageUrl)} alt="" class="banner s-15v965v">`;
	}

	$$payload.out += ` <div class="meta s-15v965v"><div class="main s-15v965v"><svg fill="none" viewBox="0 0 24 24" class="avatar s-15v965v"><defs><linearGradient id="a" x1="0" x2="100%" y1="0" y2="0" gradientTransform="rotate(45)"><stop offset="0" stop-color="#0A7AFF"></stop><stop offset="1" stop-color="#59B9FF"></stop></linearGradient></defs><path fill="url(#a)" fill-rule="evenodd" d="M11.26 5.227 5.02 6.899c-.734.197-1.17.95-.973 1.685l1.672 6.24c.197.734.951 1.17 1.685.973l6.24-1.672a1.376 1.376 0 0 0 .973-1.685L12.945 6.2a1.375 1.375 0 0 0-1.685-.973Zm-6.566.459a2.632 2.632 0 0 0-1.86 3.223l1.672 6.24a2.632 2.632 0 0 0 3.223 1.861l6.24-1.672a2.631 2.631 0 0 0 1.861-3.223l-1.672-6.24a2.632 2.632 0 0 0-3.223-1.861l-6.24 1.672Z" clip-rule="evenodd"></path><path fill="url(#a)" fill-rule="evenodd" d="M15.138 18.411a4.606 4.606 0 1 0 0-9.211 4.606 4.606 0 0 0 0 9.211Zm0 1.257a5.862 5.862 0 1 0 0-11.724 5.862 5.862 0 0 0 0 11.724Z" clip-rule="evenodd"></path></svg> <div class="info"><p class="name s-15v965v">${escape_html(record.name)}</p> <p class="creator s-15v965v">Starter pack by @${escape_html(creator.handle)}</p></div></div> <p class="description s-15v965v">${escape_html(record.description)}</p></div></a>`;
	
}

function Message$1($$payload, message) {
	$$payload.out += `<div class="message s-azdpbr">${escape_html(message)}</div>`;
}

const collectionToLabel = (collection) => {
	switch (collection) {
		case 'app.bsky.feed.post':
			return 'post';

		case 'app.bsky.feed.generator':
			return 'feed';

		case 'app.bsky.graph.list':
			return 'list';

		case 'app.bsky.graph.starterpack':
			return 'starter pack';

		case 'app.bsky.labeler.service':
			return 'labeler';
	}
	return null;
};

function Embeds($$payload, $$props) {
	

	const { post, embed, large = false } = $$props;

	function Media($$payload, embed) {
		const warning = post && findLabel(post.labels, post.author.did);

		Content_hider($$payload, {
			warning,
			children: ($$payload) => {
				if (embed.$type === 'app.bsky.embed.external#view') {
					
					External_embed($$payload, { embed });
				} else {
					

					if (embed.$type === 'app.bsky.embed.images#view') {
						
						Image_embed($$payload, { embed, standalone: true });
					} else {
						

						if (embed.$type === 'app.bsky.embed.video#view') {
							
							Video_embed($$payload, { post, embed, standalone: true });
						} else {
							
							Message$1($$payload, `Unsupported media embed`);
						}

						
					}

					
				}

				
			},
			
		});
	}

	function Record($$payload, embed) {
		const record = embed.record;

		if (record.$type === 'app.bsky.embed.record#viewRecord') {
			
			Quote_embed($$payload, { embed: record, large });
		} else {
			

			if (record.$type === 'app.bsky.feed.defs#generatorView') {
				
				Feed_embed($$payload, { embed: record });
			} else {
				

				if (record.$type === 'app.bsky.graph.defs#listView') {
					
					List_embed($$payload, { embed: record });
				} else {
					

					if (record.$type === 'app.bsky.graph.defs#starterPackViewBasic') {
						
						Starterpack_embed($$payload, { embed: record, large });
					} else {
						

						const uri = parseAtUri(record.uri);
						const resource = collectionToLabel(uri.collection);
						const isUnavailable = resource && (record.$type === 'app.bsky.embed.record#viewNotFound' || record.$type === 'app.bsky.embed.record#viewBlocked' || record.$type === 'app.bsky.embed.record#viewDetached');

						Message$1($$payload, isUnavailable ? `This ${resource} is unavailable` : `Unsupported record embed`);
					}

					
				}

				
			}

			
		}

		
	}

	$$payload.out += `<div class="embeds s-azdpbr">`;

	if (embed.$type === 'app.bsky.embed.recordWithMedia#view') {
		
		Media($$payload, embed.media);
		$$payload.out += ` `;
		Record($$payload, embed.record);
		
	} else {
		

		if (embed.$type === 'app.bsky.embed.record#view') {
			
			Record($$payload, embed);
		} else {
			
			Media($$payload, embed);
		}

		
	}

	$$payload.out += `</div>`;
	
}

const grabFirstSupported = (features) => {
	return features?.find((feature) => feature.$type === 'app.bsky.richtext.facet#link' || feature.$type === 'app.bsky.richtext.facet#mention' || feature.$type === 'app.bsky.richtext.facet#tag');
};

function Richtext_renderer($$payload, $$props) {
	

	const { text, facets, large } = $$props;
	const each_array = ensure_array_like(segmentize(text, facets));

	$$payload.out += `<p${attr("class", `${stringify(`rich-text` + (large ? ` is-large` : ` is-small`))} s-1lecfnd`)}>`;

	for (let $$index = 0, $$length = each_array.length; $$index < $$length; $$index++) {
		let segment = each_array[$$index];
		const feature = grabFirstSupported(segment.features);

		if (!feature) {
			
			$$payload.out += escape_html(segment.text);
		} else {
			

			if (feature.$type === 'app.bsky.richtext.facet#link') {
				
				$$payload.out += `<a target="_blank"${attr("href", feature.uri)} rel="noopener nofollow" class="link s-1lecfnd">${escape_html(segment.text)}</a>`;
			} else {
				

				if (feature.$type === 'app.bsky.richtext.facet#mention') {
					
					$$payload.out += `<a target="_blank"${attr("href", getProfileUrl(feature.did))} class="mention s-1lecfnd">${escape_html(segment.text)}</a>`;
				} else {
					

					if (feature.$type === 'app.bsky.richtext.facet#tag') {
						
						$$payload.out += `<a target="_blank"${attr("href", getHashtagUrl(feature.tag))} class="hashtag s-1lecfnd">${escape_html(segment.text)}</a>`;
					}

					
				}

				
			}

			
		}

		
	}

	$$payload.out += `</p>`;
	
}

function Highlighted_post($$payload, $$props) {
	

	const { post, parent, prev = false } = $$props;
	const author = post.author;
	const authorUrl = getProfileUrl(author.did);
	const authorName = author.displayName?.trim();
	const record = post.record;
	const postUrl = getPostUrl(author.did, parseAtUri(post.uri).rkey);
	const replyCount = post.replyCount || 0;
	const likeCount = post.likeCount || 0;
	const repostCount = (post.repostCount || 0) + (post.quoteCount || 0);
	const isAuthorBlurred = !!findLabel(author.labels, author.did);

	$$payload.out += `<div class="highlighted-post s-hik11q"><div class="meta s-hik11q"><a${attr("href", authorUrl)} target="_blank" class="avatar-wrapper s-hik11q">`;

	if (author.avatar) {
		
		$$payload.out += `<img loading="lazy"${attr("src", author.avatar)} alt=""${attr("class", `${stringify(`avatar` + (isAuthorBlurred ? ` is-blurred` : ``))} s-hik11q`)}>`;
	}

	$$payload.out += `</a> <a${attr("href", authorUrl)} target="_blank" class="name-wrapper s-hik11q">`;

	if (authorName) {
		
		$$payload.out += `<bdi class="display-name-wrapper s-hik11q"><span class="display-name s-hik11q">${escape_html(authorName)}</span></bdi>`;
	}

	$$payload.out += ` <span class="handle s-hik11q">@${escape_html(author.handle)}</span></a> `;

	if (!prev) {
		
		$$payload.out += `<svg class="logo s-hik11q" fill="none" viewBox="0 0 320 286"><path fill="#0A7AFF" d="M69.364 19.146c36.687 27.806 76.147 84.186 90.636 114.439 14.489-30.253 53.948-86.633 90.636-114.439C277.107-.917 320-16.44 320 32.957c0 9.865-5.603 82.875-8.889 94.729-11.423 41.208-53.045 51.719-90.071 45.357 64.719 11.12 81.182 47.953 45.627 84.785-80 82.874-106.667-44.333-106.667-44.333s-26.667 127.207-106.667 44.333c-35.555-36.832-19.092-73.665 45.627-84.785-37.026 6.362-78.648-4.149-90.071-45.357C5.603 115.832 0 42.822 0 32.957 0-16.44 42.893-.917 69.364 19.147Z"></path></svg>`;
	}

	$$payload.out += `</div> `;

	if (!prev && record.reply) {
		
		$$payload.out += `<p class="context s-hik11q">`;

		if (parent) {
			

			const author = parent.author;

			$$payload.out += `Replying to <a target="_blank"${attr("href", getProfileUrl(author.did))} dir="auto" class="s-hik11q">${escape_html(author.displayName?.trim() || `@${author.handle}`)}</a>`;
		} else {
			
			$$payload.out += `Replying to an unknown post`;
		}

		$$payload.out += `</p>`;
	}

	$$payload.out += ` `;

	Richtext_renderer($$payload, {
		text: record.text,
		facets: record.facets,
		large: true
	});

	$$payload.out += ` `;

	if (post.embed) {
		
		Embeds($$payload, { post, embed: post.embed, large: true });
	}

	$$payload.out += ` <time${attr("datetime", record.createdAt)} class="date s-hik11q">${escape_html(formatLongDate(record.createdAt))}</time> <div class="stats s-hik11q"><span class="stat s-hik11q"${attr("title", likeCount === 1 ? `${formatLongNumber(likeCount)} like` : `${formatLongNumber(likeCount)} likes`)}><svg class="icon" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" stroke-width="2" d="M12 5.768c6.162-6.25 16.725 5.358 0 14.732C-4.725 11.126 5.838-.482 12 5.768Z"></path></svg> <span>${escape_html(formatCompactNumber(likeCount))}</span></span> <span class="stat s-hik11q"${attr("title", repostCount === 1 ? `${formatLongNumber(repostCount)} repost` : `${formatLongNumber(repostCount)} reposts`)}><svg class="icon" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" stroke-linecap="square" stroke-width="2" d="m17 3 3 3-3 3M7 21l-3-3 3-3m-2 3h15v-5M4 11V6h15"></path></svg> <span>${escape_html(formatCompactNumber(repostCount))}</span></span> <div class="gap s-hik11q"></div> <a${attr("href", postUrl)} target="_blank" class="permalink s-hik11q"><span>${escape_html(!replyCount ? `View on Bluesky` : replyCount === 1 ? `Read ${formatCompactNumber(replyCount)} reply on Bluesky` : `Read ${formatCompactNumber(replyCount)} replies on Bluesky`)}</span></a></div></div>`;
	
}

function Post($$payload, $$props) {
	

	const { post, parent, prev } = $$props;
	const author = post.author;
	const authorUrl = getProfileUrl(author.did);
	const authorName = author.displayName?.trim();
	const record = post.record;
	const postUrl = getPostUrl(author.did, parseAtUri(post.uri).rkey);
	const isAuthorBlurred = !!findLabel(author.labels, author.did);

	$$payload.out += `<div class="post s-12mzi62">`;

	if (!prev) {
		
		$$payload.out += `<svg class="logo s-12mzi62" fill="none" viewBox="0 0 320 286"><path fill="#0A7AFF" d="M69.364 19.146c36.687 27.806 76.147 84.186 90.636 114.439 14.489-30.253 53.948-86.633 90.636-114.439C277.107-.917 320-16.44 320 32.957c0 9.865-5.603 82.875-8.889 94.729-11.423 41.208-53.045 51.719-90.071 45.357 64.719 11.12 81.182 47.953 45.627 84.785-80 82.874-106.667-44.333-106.667-44.333s-26.667 127.207-106.667 44.333c-35.555-36.832-19.092-73.665 45.627-84.785-37.026 6.362-78.648-4.149-90.071-45.357C5.603 115.832 0 42.822 0 32.957 0-16.44 42.893-.917 69.364 19.147Z"></path></svg>`;
	}

	$$payload.out += ` <div class="aside s-12mzi62"><a target="_blank"${attr("href", authorUrl)} class="avatar-wrapper s-12mzi62">`;

	if (author.avatar) {
		
		$$payload.out += `<img loading="lazy"${attr("src", author.avatar)} alt=""${attr("class", `${stringify(`avatar` + (isAuthorBlurred ? ` is-blurred` : ``))} s-12mzi62`)}>`;
	}

	$$payload.out += `</a> <div class="line s-12mzi62"></div></div> <div class="main s-12mzi62"><div class="meta s-12mzi62"><a${attr("href", authorUrl)} target="_blank" class="name-wrapper s-12mzi62">`;

	if (authorName) {
		
		$$payload.out += `<bdi class="display-name-wrapper s-12mzi62"><span class="display-name s-12mzi62">${escape_html(authorName)}</span></bdi>`;
	}

	$$payload.out += ` <span class="handle s-12mzi62">@${escape_html(author.handle)}</span></a> <span aria-hidden="true" class="dot s-12mzi62">·</span> <a target="_blank"${attr("href", postUrl)}${attr("title", formatLongDate(record.createdAt))} class="date s-12mzi62"><time${attr("datetime", record.createdAt)}>${escape_html(formatShortDate(record.createdAt))}</time></a></div> `;

	if (!prev && record.reply) {
		
		$$payload.out += `<p class="context s-12mzi62">`;

		if (parent) {
			

			const author = parent.author;

			$$payload.out += `Replying to <a target="_blank"${attr("href", getProfileUrl(author.did))} dir="auto" class="s-12mzi62">${escape_html(author.displayName?.trim() || `@${author.handle}`)}</a>`;
		} else {
			
			$$payload.out += `Replying to an unknown post`;
		}

		$$payload.out += `</p>`;
	}

	$$payload.out += ` `;
	Richtext_renderer($$payload, { text: record.text, facets: record.facets });
	$$payload.out += ` `;

	if (post.embed) {
		
		Embeds($$payload, { post, embed: post.embed });
	}

	$$payload.out += `</div></div>`;
	
}

function Message($$payload, msg) {
	Embed_frame($$payload, {
		children: ($$payload) => {
			$$payload.out += `<div class="message s-1q9cbx0">${escape_html(msg)}</div>`;
		},
		
	});
}

const unwrapPostThread = (data, contextless, allowUnauthenticated) => {
	const items = [];
	let i = 0;
	let il = contextless ? 1 : 2;
	let curr = data;

	while (curr) {
		if (curr.$type === 'app.bsky.feed.defs#notFoundPost' || curr.$type === 'app.bsky.feed.defs#blockedPost') {
			break;
		}

		const post = curr.post;

		if (i !== 0) {
			items[i - 1].parent = post;
		}

		if (++i > il) {
			break;
		}

		const author = post.author;

		if (!allowUnauthenticated && author.labels?.some((def) => def.val === '!no-unauthenticated')) {
			break;
		}

		items.push({ post, parent: null });
		curr = curr.parent;
	}

	return items.reverse();
};

function Bluesky_post($$payload, $$props) {
	

	const { thread, contextless, allowUnauthenticated } = $$props;
	const isPwiForbidden = !allowUnauthenticated && thread !== null && thread.$type === 'app.bsky.feed.defs#threadViewPost' && thread.post.author.labels?.some((label) => label.val === NO_UNAUTHENTICATED_LABEL);

	if (thread === null) {
		
		Message($$payload, `The post can't be found, it may have been deleted.`);
	} else {
		

		if (isPwiForbidden) {
			
			Message($$payload, `The author has requested for their posts to not be displayed on external sites.`);
		} else {
			

			const posts = unwrapPostThread(thread, contextless, allowUnauthenticated);

			Embed_frame($$payload, {
				children: ($$payload) => {
					const each_array = ensure_array_like(posts);

					

					for (let idx = 0, $$length = each_array.length; idx < $$length; idx++) {
						let { post, parent } = each_array[idx];
						const hasPrevious = idx !== 0;

						if (idx === posts.length - 1) {
							
							Highlighted_post($$payload, { post, parent, prev: hasPrevious });
						} else {
							
							Post($$payload, { post, parent, prev: hasPrevious });
						}

						
					}

					
				},
				
			});
		}

		
	}

	
	
}

const fetchPost = async (opts) => {
  const rpc = new XRPC({ handler: simpleFetchHandler({ service: opts.serviceUri ?? DEFAULT_APPVIEW_URL }) });
  const contextless = opts.contextless ?? false;
  const { data } = await rpc.get("app.bsky.feed.getPostThread", {
    signal: opts.signal,
    params: {
      uri: opts.uri,
      parentHeight: !contextless ? 2 : 1,
      depth: 0
    }
  }).catch((err) => {
    if (err instanceof XRPCError) {
      if (err.kind === "NotFound") {
        return { data: null };
      }
    }
    return Promise.reject(err);
  });
  const thread = data?.thread.$type === "app.bsky.feed.defs#threadViewPost" ? data.thread : null;
  return { thread, contextless, allowUnauthenticated: opts.allowUnauthenticated ?? false };
};
const renderPost = (data) => {
  return render(Bluesky_post, { props: data }).body;
};

export { fetchPost, renderPost };
